
// frontend/src/pages/admin/ClubsManagement/ClubsManagement.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../../../components/common/Button';
import Icon from '../../../components/common/Icon';
import Input from '../../../components/common/Input';
import Pagination from '../../../components/common/Pagination';
import Modal from '../../../components/common/Modal';
import ClubForm from '../../../components/forms/ClubForm';

import {
  getClubsApi,
  updateClubStatusApi,
  deleteClubApi,
} from '../../../api/club.api';

import {
  CLUB_STATUS_LABELS,
  CLUB_STATUS_COLORS,
} from '../../../utils/constants';

const EMPTY_FILTERS = {
  status: '',
  search: '',
};

const INITIAL_PAGINATION = {
  page: 1,
  limit: 10,
  total: 0,
  totalPages: 1,
};

export default function ClubsManagement() {
  const [clubs, setClubs] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);

  const [formOpen, setFormOpen] = useState(false);
  const [editingClub, setEditingClub] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(null);

  const [snackbar, setSnackbar] = useState('');
  const [snackbarType, setSnackbarType] = useState('success');

  const [loading, setLoading] = useState(false);

  /* =========================================================
     LOAD CLUBS
  ========================================================= */

  const loadClubs = useCallback(async () => {
    setLoading(true);

    try {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([, value]) => value !== '')
      );

      const { data } = await getClubsApi({
        ...cleanFilters,
        page: pagination.page,
        limit: pagination.limit,
      });

      const clubData = data?.data || [];
      const meta = data?.meta || {};

      setClubs(clubData);

      setPagination((prev) => ({
        ...prev,
        total: meta.total ?? 0,
        totalPages: Math.max(meta.totalPages ?? 1, 1),
      }));
    } catch (err) {
      setClubs([]);

      showSnackbar(
        err?.response?.data?.message ||
          'خطا در دریافت اطلاعات باشگاه‌ها',
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => {
    loadClubs();
  }, [loadClubs]);

  /* =========================================================
     SNACKBAR
  ========================================================= */

  const showSnackbar = (message, type = 'success') => {
    setSnackbar(message);
    setSnackbarType(type);

    window.clearTimeout(showSnackbar.timer);

    showSnackbar.timer = window.setTimeout(() => {
      setSnackbar('');
    }, 4000);
  };

  /* =========================================================
     FILTERS
  ========================================================= */

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));

    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);

    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  /* =========================================================
     PAGINATION
  ========================================================= */

  const handlePageChange = (newPage) => {
    if (
      newPage < 1 ||
      newPage > pagination.totalPages ||
      newPage === pagination.page
    ) {
      return;
    }

    setPagination((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  /* =========================================================
     CREATE / EDIT
  ========================================================= */

  const openCreateForm = () => {
    setEditingClub(null);
    setFormOpen(true);
  };

  const openEditForm = (club) => {
    setEditingClub(club);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingClub(null);
  };

  /* =========================================================
     STATUS
  ========================================================= */

  const handleStatusChange = async (id, newStatus) => {
    const previousClub = clubs.find((club) => club.id === id);

    if (!previousClub || previousClub.status === newStatus) {
      return;
    }

    setStatusUpdating(id);

    // Optimistic update
    setClubs((prev) =>
      prev.map((club) =>
        club.id === id
          ? {
              ...club,
              status: newStatus,
            }
          : club
      )
    );

    try {
      await updateClubStatusApi(id, newStatus);

      showSnackbar('وضعیت باشگاه با موفقیت بروزرسانی شد');
    } catch (err) {
      // Rollback
      setClubs((prev) =>
        prev.map((club) =>
          club.id === id
            ? {
                ...club,
                status: previousClub.status,
              }
            : club
        )
      );

      showSnackbar(
        err?.response?.data?.message ||
          'بروزرسانی وضعیت باشگاه ناموفق بود',
        'error'
      );
    } finally {
      setStatusUpdating(null);
    }
  };

  /* =========================================================
     DELETE / DEACTIVATE
  ========================================================= */

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await deleteClubApi(deleteTarget);

      setDeleteTarget(null);

      showSnackbar('باشگاه با موفقیت غیرفعال شد');

      // اگر آخرین آیتم صفحه حذف شد، به صفحه قبل برگرد
      if (clubs.length === 1 && pagination.page > 1) {
        setPagination((prev) => ({
          ...prev,
          page: prev.page - 1,
        }));
      } else {
        await loadClubs();
      }
    } catch (err) {
      showSnackbar(
        err?.response?.data?.message ||
          'غیرفعال‌ سازی باشگاه انجام نشد',
        'error'
      );
    }
  };

  /* =========================================================
     STATISTICS
  ========================================================= */

  const statistics = useMemo(() => {
    return {
      total: pagination.total,

      active: clubs.filter(
        (club) => club.status === 'ACTIVE'
      ).length,

      pending: clubs.filter(
        (club) => club.status === 'PENDING'
      ).length,

      inactive: clubs.filter(
        (club) =>
          club.status === 'INACTIVE' ||
          club.status === 'SUSPENDED'
      ).length,
    };
  }, [clubs, pagination.total]);

  /* =========================================================
     STATUS COLOR
  ========================================================= */

  const getStatusColor = (status) => {
    return (
      CLUB_STATUS_COLORS?.[status] ||
      'bg-slate-100 text-slate-600'
    );
  };

  /* =========================================================
     PHOTO URL
  ========================================================= */

  const getLogoUrl = (logo) => {
    if (!logo) return null;

    if (
      logo.startsWith('http://') ||
      logo.startsWith('https://')
    ) {
      return logo;
    }

    const baseURL =
      process.env.REACT_APP_API_BASE_URL
        ?.replace('/api/v1', '')
        ?.replace(/\/$/, '') || '';

    return `${baseURL}${logo.startsWith('/') ? logo : `/${logo}`}`;
  };

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className="space-y-6">

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">

        <div className="flex items-center gap-3">

          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-emerald-50 text-emerald-600">
            <Icon
              name="building"
              className="h-6 w-6"
            />
          </div>

          <div>
            <h1 className="text-2xl font-black text-text-dark">
              مدیریت باشگاه‌ها
            </h1>

            <p className="mt-1 text-sm text-slate-500">
              مدیریت، بررسی و کنترل باشگاه‌های ثبت‌شده در سامانه
            </p>
          </div>

        </div>

        <Button
          variant="primary"
          onClick={openCreateForm}
          className="h-11 rounded-xl px-5"
        >
          <Icon
            name="plus"
            className="ml-2 h-4 w-4"
          />
          ثبت باشگاه جدید
        </Button>

      </div>

      {/* =====================================================
          STATISTICS
      ===================================================== */}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">

        <StatCard
          title="کل باشگاه‌ها"
          value={statistics.total}
          icon="building"
          color="emerald"
        />

        <StatCard
          title="فعال"
          value={statistics.active}
          icon="check-circle"
          color="green"
        />

        <StatCard
          title="در انتظار تأیید"
          value={statistics.pending}
          icon="clock"
          color="amber"
        />

        <StatCard
          title="غیرفعال / معلق"
          value={statistics.inactive}
          icon="archive"
          color="slate"
        />

      </div>

      {/* =====================================================
          FILTERS
      ===================================================== */}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">

        <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">

          <div className="flex items-center gap-2">

            <div className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-50 text-emerald-600">
              <Icon
                name="filter"
                className="h-4 w-4"
              />
            </div>

            <div>
              <h2 className="text-sm font-black text-text-dark">
                فیلتر و جستجوی باشگاه‌ها
              </h2>

              <p className="mt-0.5 text-[11px] text-slate-400">
                باشگاه موردنظر خود را سریع پیدا کنید
              </p>
            </div>

          </div>

          {(filters.status || filters.search) && (
            <button
              type="button"
              onClick={resetFilters}
              className="flex items-center gap-1 text-xs font-bold text-slate-400 transition hover:text-red-500"
            >
              <Icon
                name="close"
                className="h-3.5 w-3.5"
              />
              پاک کردن فیلترها
            </button>
          )}

        </div>

        <div className="grid gap-3 p-5 sm:grid-cols-2">

          {/* Status */}

          <select
            value={filters.status}
            onChange={(e) =>
              updateFilter('status', e.target.value)
            }
            className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-text-dark outline-none transition hover:border-slate-300 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/20"
          >
            <option value="">
              همه وضعیت‌ها
            </option>

            {Object.entries(CLUB_STATUS_LABELS).map(
              ([key, label]) => (
                <option
                  key={key}
                  value={key}
                >
                  {label}
                </option>
              )
            )}

          </select>

          {/* Search */}

          <div className="relative">

            <Input
              placeholder="جستجوی نام باشگاه..."
              value={filters.search}
              onChange={(e) =>
                updateFilter('search', e.target.value)
              }
              className="h-11! pr-10"
            />

            <Icon
              name="search"
              className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            />

          </div>

        </div>

      </div>

      {/* =====================================================
          SNACKBAR
      ===================================================== */}

      {snackbar && (
        <div
          className={`flex items-center justify-between rounded-xl border px-4 py-3 text-sm font-medium ${
            snackbarType === 'error'
              ? 'border-red-100 bg-red-50 text-red-700'
              : 'border-emerald-100 bg-emerald-50 text-emerald-700'
          }`}
        >

          <div className="flex items-center gap-2">

            <Icon
              name={
                snackbarType === 'error'
                  ? 'alert-circle'
                  : 'check-circle'
              }
              className="h-4 w-4"
            />

            <span>{snackbar}</span>

          </div>

          <button
            type="button"
            onClick={() => setSnackbar('')}
            className="rounded-lg p-1 transition hover:bg-black/5"
          >
            <Icon
              name="close"
              className="h-4 w-4"
            />
          </button>

        </div>
      )}

      {/* =====================================================
          TABLE
      ===================================================== */}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">

        <div className="flex flex-col gap-2 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">

          <div>
            <h2 className="font-black text-text-dark">
              فهرست باشگاه‌ها
            </h2>

            <p className="mt-1 text-xs text-slate-400">
              {pagination.total.toLocaleString('fa-IR')}
              {' '}
              باشگاه ثبت شده
            </p>
          </div>

          {loading && (
            <div className="flex items-center gap-2 text-xs font-bold text-emerald-600">

              <span className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />

              در حال بروزرسانی...
            </div>
          )}

        </div>

        {/* Desktop */}

        <div className="hidden overflow-x-auto md:block">

          <table className="w-full text-right text-sm">

            <thead>
              <tr className="border-b border-slate-200 bg-gradient-to-l from-slate-50 via-white to-emerald-50/40">

                <th className="px-5 py-4 text-xs font-black text-slate-500">
                  باشگاه
                </th>

                <th className="px-5 py-4 text-xs font-black text-slate-500">
                  مدیر
                </th>

                <th className="px-5 py-4 text-xs font-black text-slate-500">
                  ایمیل
                </th>

                <th className="px-5 py-4 text-xs font-black text-slate-500">
                  تلفن
                </th>

                <th className="px-5 py-4 text-xs font-black text-slate-500">
                  شهر
                </th>

                <th className="px-5 py-4 text-xs font-black text-slate-500">
                  وضعیت
                </th>

                <th className="px-5 py-4 text-center text-xs font-black text-slate-500">
                  عملیات
                </th>

              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">

              {loading ? (
                <ClubsTableSkeleton />
              ) : clubs.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="py-16 text-center"
                  >
                    <EmptyClubs onAdd={openCreateForm} />
                  </td>
                </tr>
              ) : (
                clubs.map((club) => {

                  const logoUrl = getLogoUrl(club.logo);

                  return (
                    <tr
                      key={club.id}
                      className="group transition-all duration-200 hover:bg-emerald-50/40"
                    >

                      {/* Club */}

                      <td className="px-5 py-4">

                        <div className="flex items-center gap-3">

                          <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-700 shadow-md shadow-emerald-500/20">

                            {logoUrl ? (
                              <img
                                src={logoUrl}
                                alt={club.name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-white">
                                <Icon
                                  name="building"
                                  className="h-5 w-5"
                                />
                              </div>
                            )}

                          </div>

                          <div className="min-w-0">

                            <p className="font-black text-slate-800">
                              {club.name || '-'}
                            </p>

                            {club.licenseNumber && (
                              <p className="mt-1 text-[11px] text-slate-400">
                                مجوز: {club.licenseNumber}
                              </p>
                            )}

                          </div>

                        </div>

                      </td>

                      {/* Manager */}

                      <td className="px-5 py-4">

                        <div>

                          <p className="font-semibold text-slate-700">
                            {club.manager?.fullName || '-'}
                          </p>

                          {club.manager?.phone && (
                            <p className="mt-1 text-[11px] text-slate-400">
                              {club.manager.phone}
                            </p>
                          )}

                        </div>

                      </td>

                      {/* Email */}

                      <td className="px-5 py-4 text-slate-600">
                        {club.manager?.email ||
                          club.email ||
                          '-'}
                      </td>

                      {/* Phone */}

                      <td className="px-5 py-4 text-slate-600">
                        {club.phone || '-'}
                      </td>

                      {/* City */}

                      <td className="px-5 py-4">

                        <span className="rounded-xl bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600">
                          {club.city || '-'}
                        </span>

                      </td>

                      {/* Status */}

                      <td className="px-5 py-4">

                        <select
                          value={club.status || ''}
                          disabled={
                            statusUpdating === club.id
                          }
                          onChange={(e) =>
                            handleStatusChange(
                              club.id,
                              e.target.value
                            )
                          }
                          className={`rounded-full border-0 px-3 py-1.5 text-xs font-bold outline-none ring-0 transition ${getStatusColor(
                            club.status
                          )} ${
                            statusUpdating === club.id
                              ? 'cursor-wait opacity-60'
                              : 'cursor-pointer'
                          }`}
                        >

                          {Object.entries(
                            CLUB_STATUS_LABELS
                          ).map(([key, label]) => (
                            <option
                              key={key}
                              value={key}
                            >
                              {label}
                            </option>
                          ))}

                        </select>

                      </td>

                      {/* Actions */}

                      <td className="px-5 py-4">

                        <div className="flex items-center justify-center gap-1">

                          <ActionButton
                            icon="edit"
                            title="ویرایش"
                            color="blue"
                            onClick={() =>
                              openEditForm(club)
                            }
                          />

                          <ActionButton
                            icon="trash"
                            title="غیرفعال‌سازی"
                            color="red"
                            onClick={() =>
                              setDeleteTarget(club.id)
                            }
                          />

                        </div>

                      </td>

                    </tr>
                  );
                })
              )}

            </tbody>

          </table>

        </div>

        {/* Mobile */}

        <div className="grid gap-3 p-4 md:hidden">

          {loading ? (
            <ClubsMobileSkeleton />
          ) : clubs.length === 0 ? (
            <EmptyClubs onAdd={openCreateForm} />
          ) : (
            clubs.map((club) => {

              const logoUrl = getLogoUrl(club.logo);

              return (
                <div
                  key={club.id}
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-md"
                >

                  <div className="flex items-center gap-3">

                    <div className="h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-700">

                      {logoUrl ? (
                        <img
                          src={logoUrl}
                          alt={club.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-white">
                          <Icon
                            name="building"
                            className="h-5 w-5"
                          />
                        </div>
                      )}

                    </div>

                    <div className="min-w-0 flex-1">

                      <h3 className="truncate font-black text-slate-800">
                        {club.name || '-'}
                      </h3>

                      <p className="mt-1 text-xs text-slate-400">
                        {club.manager?.fullName ||
                          'مدیر ثبت نشده'}
                      </p>

                    </div>

                    <select
                      value={club.status || ''}
                      disabled={
                        statusUpdating === club.id
                      }
                      onChange={(e) =>
                        handleStatusChange(
                          club.id,
                          e.target.value
                        )
                      }
                      className={`max-w-[110px] rounded-full border-0 px-2 py-1 text-[10px] font-bold outline-none ${getStatusColor(
                        club.status
                      )}`}
                    >

                      {Object.entries(
                        CLUB_STATUS_LABELS
                      ).map(([key, label]) => (
                        <option
                          key={key}
                          value={key}
                        >
                          {label}
                        </option>
                      ))}

                    </select>

                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs">

                    <InfoItem
                      icon="phone"
                      label={club.phone || 'بدون تلفن'}
                    />

                    <InfoItem
                      icon="map-pin"
                      label={club.city || 'شهر ثبت نشده'}
                    />

                  </div>

                  <div className="mt-3 flex items-center justify-end gap-1 border-t border-slate-100 pt-3">

                    <ActionButton
                      icon="edit"
                      title="ویرایش"
                      color="blue"
                      onClick={() =>
                        openEditForm(club)
                      }
                    />

                    <ActionButton
                      icon="trash"
                      title="غیرفعال‌سازی"
                      color="red"
                      onClick={() =>
                        setDeleteTarget(club.id)
                      }
                    />

                  </div>

                </div>
              );
            })
          )}

        </div>

        {/* Pagination */}

        {!loading && clubs.length > 0 && (
          <div className="border-t border-slate-100 px-4 py-4">
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}

      </div>

      {/* =====================================================
          CLUB FORM
      ===================================================== */}

      <ClubForm
        open={formOpen}
        onClose={closeForm}
        onSaved={async () => {
          closeForm();
          await loadClubs();
        }}
        club={editingClub}
      />

      {/* =====================================================
          DELETE MODAL
      ===================================================== */}

      <Modal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="غیرفعال‌سازی باشگاه"
      >

        <div className="space-y-5">

          <div className="flex items-start gap-3 rounded-xl bg-red-50 p-4">

            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-red-100 text-red-600">
              <Icon
                name="alert-circle"
                className="h-5 w-5"
              />
            </div>

            <div>

              <p className="font-bold text-red-700">
                آیا از غیرفعال‌سازی این باشگاه مطمئن هستید؟
              </p>

              <p className="mt-1 text-xs leading-6 text-red-500">
                باشگاه حذف دائمی نمی‌شود و وضعیت آن به
                غیرفعال تغییر خواهد کرد.
              </p>

            </div>

          </div>

          <div className="flex justify-end gap-2">

            <Button
              variant="ghost"
              onClick={() =>
                setDeleteTarget(null)
              }
            >
              انصراف
            </Button>

            <Button
              variant="primary"
              onClick={handleDelete}
            >
              غیرفعال‌سازی
            </Button>

          </div>

        </div>

      </Modal>

    </div>
  );
}

/* =========================================================
   STAT CARD
========================================================= */

function StatCard({
  title,
  value,
  icon,
  color = 'emerald',
}) {
  const colors = {
    emerald:
      'bg-emerald-50 text-emerald-600',
    green:
      'bg-green-50 text-green-600',
    amber:
      'bg-amber-50 text-amber-600',
    slate:
      'bg-slate-100 text-slate-600',
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">

      <div className="flex items-center justify-between">

        <div>

          <p className="text-xs font-bold text-slate-400">
            {title}
          </p>

          <p className="mt-2 text-2xl font-black text-text-dark">
            {Number(value || 0).toLocaleString('fa-IR')}
          </p>

        </div>

        <div
          className={`grid h-11 w-11 place-items-center rounded-xl ${
            colors[color] || colors.emerald
          }`}
        >
          <Icon
            name={icon}
            className="h-5 w-5"
          />
        </div>

      </div>

    </div>
  );
}

/* =========================================================
   ACTION BUTTON
========================================================= */

function ActionButton({
  icon,
  title,
  color,
  onClick,
}) {
  const colors = {
    blue:
      'text-blue-600 hover:bg-blue-50',
    red:
      'text-red-500 hover:bg-red-50',
    emerald:
      'text-emerald-600 hover:bg-emerald-50',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`grid h-9 w-9 place-items-center rounded-xl transition-all duration-200 hover:scale-105 ${
        colors[color] || colors.emerald
      }`}
    >
      <Icon
        name={icon}
        className="h-4 w-4"
      />
    </button>
  );
}

/* =========================================================
   INFO ITEM
========================================================= */

function InfoItem({
  icon,
  label,
}) {
  return (
    <div className="flex min-w-0 items-center gap-1.5 rounded-xl bg-slate-50 px-2.5 py-2 text-slate-500">

      <Icon
        name={icon}
        className="h-3.5 w-3.5 shrink-0 text-slate-400"
      />

      <span className="truncate text-[11px]">
        {label}
      </span>

    </div>
  );
}

/* =========================================================
   DESKTOP SKELETON
========================================================= */

function ClubsTableSkeleton() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, index) => (
        <tr
          key={index}
          className="animate-pulse border-b border-slate-100"
        >

          <td className="px-5 py-4">
            <div className="flex items-center gap-3">

              <div className="h-11 w-11 rounded-xl bg-slate-200" />

              <div className="space-y-2">
                <div className="h-3 w-32 rounded bg-slate-200" />
                <div className="h-2.5 w-20 rounded bg-slate-100" />
              </div>

            </div>
          </td>

          <td className="px-5 py-4">
            <div className="h-3 w-24 rounded bg-slate-200" />
          </td>

          <td className="px-5 py-4">
            <div className="h-3 w-32 rounded bg-slate-200" />
          </td>

          <td className="px-5 py-4">
            <div className="h-3 w-24 rounded bg-slate-200" />
          </td>

          <td className="px-5 py-4">
            <div className="h-6 w-16 rounded-full bg-slate-200" />
          </td>

          <td className="px-5 py-4">
            <div className="h-7 w-20 rounded-full bg-slate-200" />
          </td>

          <td className="px-5 py-4">
            <div className="flex justify-center gap-2">
              <div className="h-8 w-8 rounded-xl bg-slate-200" />
              <div className="h-8 w-8 rounded-xl bg-slate-200" />
            </div>
          </td>

        </tr>
      ))}
    </>
  );
}

/* =========================================================
   MOBILE SKELETON
========================================================= */

function ClubsMobileSkeleton() {
  return (
    <>
      {Array.from({ length: 4 }).map((_, index) => (
        <div
          key={index}
          className="animate-pulse rounded-2xl border border-slate-200 p-4"
        >

          <div className="flex items-center gap-3">

            <div className="h-12 w-12 rounded-xl bg-slate-200" />

            <div className="flex-1 space-y-2">
              <div className="h-3 w-32 rounded bg-slate-200" />
              <div className="h-2.5 w-24 rounded bg-slate-100" />
            </div>

            <div className="h-6 w-20 rounded-full bg-slate-200" />

          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="h-8 rounded-xl bg-slate-100" />
            <div className="h-8 rounded-xl bg-slate-100" />
          </div>

        </div>
      ))}
    </>
  );
}

/* =========================================================
   EMPTY
========================================================= */

function EmptyClubs({
  onAdd,
}) {
  return (
    <div className="flex flex-col items-center justify-center">

      <div className="grid h-16 w-16 place-items-center rounded-2xl bg-slate-100 text-slate-400">
        <Icon
          name="building"
          className="h-7 w-7"
        />
      </div>

      <h3 className="mt-4 font-black text-text-dark">
        باشگاهی پیدا نشد
      </h3>

      <p className="mt-2 max-w-sm text-sm leading-6 text-slate-400">
        فیلترها را تغییر دهید یا اولین باشگاه را در سامانه ثبت کنید.
      </p>

      <Button
        variant="primary"
        className="mt-5"
        onClick={onAdd}
      >
        <Icon
          name="plus"
          className="ml-2 h-4 w-4"
        />
        ثبت باشگاه جدید
      </Button>

    </div>
  );
}
